export interface TooltipContent {
  title?: string;
  content: string;
  ctaText?: string;
  ctaUrl?: string;
  video?: string;
}

export const TOOLTIP_CONTENT: Record<string, Record<string, TooltipContent>> = {
  apc: {
    body_composition_visual: {
      title: 'Body Composition',
      content:
        "<p>This graph shows the different compartments which can produce and take up lactate and which don't.</p><p><strong>Non H2O</strong> means the fraction of the body which is not made up of water and therefore doesn’t take up any lactate.</p><p><strong>H2O space</strong> is the water content of the body which is not taking up any lactate.</p><p><strong>Passive lactate</strong> refers to fraction of the body which can take up lactate and oxidize but not actively produces it.</p><p><strong>Active muscle</strong> refers to muscle mass involved in exercise and therefore both produces and oxidizes lactate.</p>",
      ctaText: 'Learn more about body composition and how it affects performance',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005927-body-composition-data',
      video: '',
    },
    body_composition_table: {
      title: 'Body Composition',
      content:
        '<p>The body composition table explains the proportion of fat and fat free body compartment.</p><p><strong>BMI</strong> stands for Body Mass Index.</p><p><strong>Projected BSA</strong> is the projected body surface area.</p>',
      ctaText: 'Learn more about body composition',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005927-body-composition-data',
      video: '',
    },
    fingerprint: {
      title: 'Metabolic Fingerprint',
      content:
        '<p>The metabolic fingerprint compares physiological benchmarks with a chosen comparison group.</p><p>All metrics are normalized to a scale from 0 (low) to 100 (high). Note that high is not always better.</p><p>Normalization is based on athlete category such as professional, amateur, or recreational.</p>',
      ctaText: 'Learn more about metabolic fingerprint',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005927-body-composition-data',
      video: '',
    },
    performance_development: {
      title: 'Performance Development',
      content:
        '<p>The Performance Development chart tracks physiological benchmarks over time.</p><p>It allows comparison of changes in physiological data with training performed.</p><p>You can customize displayed metrics and configure dashboards.</p>',
      ctaText: 'Customize your performance development chart',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/11488667-performance-development-chart',
      video: '',
    },
    vo2max: {
      title: 'VO2max - maximum aerobic power',
      content:
        '<p>VO2max represents the maximum volume of oxygen an individual can utilize during exercise.</p><p>It is a key measurement for endurance performance, longevity, and health.</p><p>Higher VO2max allows higher speed or power using aerobic metabolism.</p>',
      ctaText: 'Take the free course on VO2max',
      ctaUrl: 'https://app.inscyd.com/home/college/course?course_id=218',
      video: '',
    },
    vlamax: {
      title: 'VLamax - maximum glycolytic power',
      content:
        '<p>VLamax represents the maximum energy production rate of glycolysis.</p><p>A high VLamax supports short intense efforts like sprints, while a low VLamax supports endurance and fat metabolism.</p>',
      ctaText: 'Take the free course on VLamax',
      ctaUrl: 'https://app.inscyd.com/home/college/course?course_id=213',
      video: '',
    },
    at: {
      title: 'AT - Anaerobic Threshold (MLSS)',
      content:
        '<p>AT (Anaerobic Threshold / MLSS) represents the highest intensity where lactate production and clearance are balanced.</p><p>At this point, lactate levels remain stable over time.</p>',
      ctaText: 'Learn more about anaerobic threshold',
      ctaUrl: '',
      video: '',
    },
    fatmax: {
      title: 'FatMax & MFO',
      content:
        '<p>FatMax is the intensity at which fat combustion is highest.</p><p>MFO (Maximum Fat Oxidation) is the actual fat burning rate at this intensity.</p>',
      ctaText: 'Take the free course on fat metabolism',
      ctaUrl: 'https://app.inscyd.com/home/college/course?course_id=219',
      video: '',
    },
    carbmax: {
      title: 'Carbmax - carbohydrate metabolism',
      content:
        '<p>CarbMax is the intensity where the athlete uses 90g of carbohydrates per hour.</p><p>It helps compare carbohydrate usage efficiency between athletes.</p>',
      ctaText: 'Read detailed CarbMax article',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005960-carbmax-carbohydrate-metabolism',
      video: '',
    },
    glycogen_available: {
      title: 'Available Glycogen g/kg',
      content:
        '<p>Glycogen is stored carbohydrate in muscles and liver.</p><p>Only glycogen available to specific muscles during exercise is relevant.</p>',
      ctaText: 'Learn more about glycogen',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005958-available-glycogen',
      video: '',
    },
    lt1: {
      title: 'LT1',
      content:
        '<p>LT1 is the first increase in blood lactate during an incremental test.</p><p>It depends on test protocol, so INSCYD standardizes calculations.</p>',
      ctaText: 'Learn more about LT1',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005957-lt1',
      video: '',
    },
    accumulation_index: {
      title: 'Accumulation Index',
      content:
        '<p>The accumulation index describes how fast lactate accumulates above threshold.</p><p>A lower index means slower accumulation and longer endurance above threshold.</p>',
      ctaText: 'Watch video about accumulation index',
      ctaUrl: '',
      video: 'https://youtu.be/fFdFCoeJ0Us',
    },
    recovery_index: {
      title: 'Recovery Index',
      content:
        '<p>The recovery index describes how quickly an athlete can clear lactate and at what intensity.</p><p>Higher values indicate faster recovery at higher intensity.</p>',
      ctaText: 'Watch recovery index video',
      ctaUrl: '',
      video: 'https://youtu.be/uR54kABaO7w',
    },
    w_prime: {
      title: "W'",
      content:
        "<p>W' refers to the amount of energy an athlete can spend when exercising above critical power or speed.</p><p>It was initially considered a constant anaerobic energy store, but is now understood to depend on effort duration and includes aerobic contributions.</p>",
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    body_fat: {
      title: 'Body Fat',
      content: '<p>Body Fat represents the percentage of the body which is made of fat.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    max_aerobic_speed: {
      title: 'Maximum Aerobic Speed',
      content:
        '<p>Maximum Aerobic Speed (MAP) refers to the power output or speed that elicits VO2max.</p><p>It represents the highest aerobic intensity that can be sustained for a given duration or distance.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    max_lactate: {
      title: 'Max Lactate',
      content:
        '<p>Max Lactate is the highest lactate concentration measured during an all-out effort.</p><p>It is typically reached after efforts lasting between 3 to 15 minutes.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    metabolic_power: {
      title: 'Metabolic Power',
      content:
        '<p>Metabolic Power displays key performance benchmarks in a unified unit such as power or speed.</p><p>It allows direct comparison between metrics like VO2max, VLamax, MLSS, FatMax, and CarbMax by normalizing them into the same unit.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
  },
  load_characteristics: {
    metabolic_demand_vo2: {
      title: 'Metabolic demand & VO2',
      content:
        '<p>The dark blue line indicates the total metabolic cost (energy cost) as a function of power or speed.</p><p>Instead of expressing it in energy (kcal or kJ), it is shown in oxygen equivalents.</p><p>The light blue line represents oxygen uptake (VO2).</p><p>The difference between these curves indicates how much energy is covered aerobically, while the gap represents anaerobic contribution.</p>',
      ctaText: 'Read article about load characteristic graphs',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005971-interpretation-of-the-load-characteristic-graphs',
      video: '',
    },
    lactate_production_max_oxidation: {
      title: 'Lactate production & max oxidation',
      content:
        '<p>This graph shows lactate production rate (red curve) and maximum lactate oxidation rate (blue curve) as a function of intensity.</p><p>The intersection point marks the maximum lactate steady state (MLSS), where production equals oxidation.</p><p>Above MLSS, lactate accumulates continuously as production exceeds oxidation.</p><p>Below MLSS, lactate can be reused or fat can be used as fuel.</p>',
      ctaText: 'Read article about load characteristic graphs',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005971-interpretation-of-the-load-characteristic-graphs',
      video: '',
    },
    lactate_recovery_accumulation: {
      title: 'Lactate recovery & accumulation',
      content:
        '<p>This graph illustrates the net lactate accumulation rate (purple curve) above MLSS.</p><p>The grey curve shows the rate at which accumulated lactate can be cleared or oxidized.</p>',
      ctaText: 'Read article about load characteristic graphs',
      ctaUrl: '',
      video: '',
    },
    fat_carbohydrate_combustion: {
      title: 'Fat & carbohydrate combustion',
      content:
        '<p>This graph shows fat combustion rate (green curve) and carbohydrate combustion rate (red curve) across exercise intensity.</p><p>Values are displayed in kcal/h and additionally carbohydrates in g/h.</p><p>The green area highlights maximum fat oxidation (MFO ±5%).</p><p>The orange area indicates carbohydrate usage between 60–90 g/h.</p>',
      ctaText: 'Read article about load characteristic graphs',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005971-interpretation-of-the-load-characteristic-graphs',
      video: '',
    },
    aerobic_anaerobic_energy_contribution: {
      title: 'Aerobic & Anaerobic energy contribution',
      content:
        '<p>This graph shows the fractional contribution of aerobic (blue) and anaerobic (red) metabolism.</p><p>It represents steady-state conditions, where anaerobic contribution comes from glycolysis only.</p>',
      ctaText: 'Read article about load characteristic graphs',
      ctaUrl: '',
      video: '',
    },
    fractional_energy_fat_carbs: {
      title: 'Fractional energy contribution from Fat and Carbohydrates',
      content:
        '<p>This graph shows the proportion of energy derived from fats (green) and carbohydrates (red).</p><p>It illustrates how fuel usage shifts with exercise intensity.</p>',
      ctaText: 'Read article about load characteristic graphs',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005971-interpretation-of-the-load-characteristic-graphs',
      video: '',
    },
  },
  lactate_data: {
    test_protocol_kinetics: {
      title: 'Test Protocol and Kinetics of Lactate and VO2',
      content:
        '<p>The grey bars visualize the test protocol used.</p><p>The width of each bar represents duration, and the height represents intensity.</p><p>The red curve shows calculated lactate kinetics, while red dots indicate measured lactate values.</p><p>The blue graph represents muscular oxygen kinetics. If VO2 data is available, measured values are shown as blue dots.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    metabolic_demand_plot: {
      title: 'Metabolic Demand Plot',
      content:
        '<p>This graph shows measured total energy demand as oxygen equivalents (red dots).</p><p>Anaerobic energy is converted into oxygen equivalents to represent total energy demand.</p><p>The blue line represents the fitted energy demand curve, indicating efficiency or energy cost as a function of speed.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    lactate_fitting: {
      title: 'Lactate Curves - Measured and calculated data',
      content:
        '<p>This graph compares calculated lactate curves with measured lactate concentrations.</p><p>Measured efforts are shown as dots, while calculated values are represented as lines.</p><p>The model considers VO2max, VLamax, body composition, sport type, duration, intensity, and physiological factors.</p><p>Closer alignment between measured and calculated values indicates higher model accuracy.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    raw_data: {
      title: 'Raw data',
      content:
        '<p>The upper section provides an overview of raw test data including duration, intensity, lactate concentration, heart rate, and VO2.</p><p>The lower section shows calculated values:</p><ul><li><strong>VO2tot</strong>: total oxygen demand</li><li><strong>%aerobic / %anaerobic</strong>: energy contribution split</li><li><strong>%MLSS</strong>: intensity relative to maximum lactate steady state</li><li><strong>%VO2max</strong>: intensity relative to maximum oxygen uptake</li></ul>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
    heart_rate_correlation: {
      title: 'Heart Rate Correlation',
      content:
        '<p>This graph relates measured heart rate to exercise intensity (speed, power, or pace).</p><p>This relationship is used to determine heart rate-based training zones.</p>',
      ctaText: '',
      ctaUrl: '',
      video: '',
    },
  },
  ppd_data: {
    cross_validation: {
      title: 'Cross Validation',
      content:
        '<p>This graph shows the result of an automatic self-audit of the raw data.</p><p>It includes three key metrics: VLamax, VO2max, and Critical Power.</p><p>Each metric is derived from specific test efforts:</p><ul><li>Sprint efforts → VLamax</li><li>Long all-out efforts → VO2max</li><li>All maximal efforts → Critical Power</li></ul><p>The x-axis shows values derived directly from test efforts.</p><p>The y-axis shows calculated values derived from the other two metrics.</p><p>The black line represents perfect agreement between measured and calculated values.</p>',
      ctaText: 'Read detailed explanation',
      ctaUrl: 'https://helpcenter.inscyd.com/en/articles/9005943-cross-validation-of-test-data-a-self-auditing-system',
      video: '',
    },
    accuracy_check: {
      title: 'Accuracy check',
      content:
        '<p>Traffic light indicators show the expected accuracy of VO2max, VLamax, and Critical Power.</p><p>The assessment is based on the cross-validation results.</p>',
      ctaText: 'Learn more',
      ctaUrl:
        'https://helpcenter.inscyd.com/en/articles/12002374-accuracy-check-in-ppd-testing-the-traffic-light-system',
      video: '',
    },
    heart_rate_analysis: {
      title: 'Heart Rate Analysis',
      content:
        '<p>This graph displays all measured heart rate data from the test files.</p><p>Due to physiological variability, the data appears as a cloud rather than a single line.</p><p>A regression line is fitted to represent the best heart rate for a given intensity.</p><p><strong>R²</strong> indicates how well the model fits the data.</p><p><strong>Slope</strong> represents how quickly heart rate increases with intensity.</p><p><strong>Offset</strong> indicates bias in the regression.</p><p>The shifting value shows how long (in minutes) heart rate takes to adapt to changes in intensity.</p>',
      ctaText: 'Read detailed article',
      ctaUrl: '',
      video: '',
    },
  },
  training_zones: {},
};
